import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  cssClass?: string;
  sortable?: boolean;
  customTemplate?: boolean;
  customHeaderTemplate?: boolean;
}

export interface TableRow {
  [key: string]: any;
}

export interface RowReorderEvent {
  rows: TableRow[];
  previousIndex: number;
  currentIndex: number;
}

export interface SortEvent {
  column: string;
  direction: 'asc' | 'desc' | null;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() draggableRows = false;
  @Input() dragPreviewLabelGetter: ((row: TableRow) => string) | null = null;
  @Output() rowsReordered = new EventEmitter<RowReorderEvent>();

  private _rows: TableRow[] = [];

  @Input()
  set rows(rows: TableRow[]) {
    this._rows = rows
      ? rows.map((row) => ({
          ...row,
          dragPreviewLabel: this.computeDragPreviewLabel(row)
        }))
      : [];
  }

  get rows(): TableRow[] {
    return this._rows;
  }
  @Input() showDeleteAction = false;
  @Input() customCellTemplates: { [key: string]: TemplateRef<any> } = {};
  @Input() customHeaderTemplates: { [key: string]: TemplateRef<any> } = {};
  @Output() rowClick = new EventEmitter<TableRow>();
  @Output() rowDelete = new EventEmitter<TableRow>();
  @Output() sort = new EventEmitter<SortEvent>();

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  getValue(row: TableRow, columnKey: string): string {
    const value = row[columnKey];
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  onRowClick(row: TableRow): void {
    this.rowClick.emit(row);
  }

  onSort(column: TableColumn): void {
    if (column.sortable === false) {
      return;
    }

    if (this.sortColumn === column.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column.key;
      this.sortDirection = 'asc';
    }

    this.sort.emit({
      column: this.sortColumn,
      direction: this.sortDirection
    });
  }

  getSortIcon(column: TableColumn): string {
    if (column.sortable === false) {
      return '';
    }

    if (this.sortColumn !== column.key) {
      return '';
    }

    return this.sortDirection === 'asc' ? 'expand_less' : 'expand_more';
  }

  onDeleteClick(row: TableRow): void {
    this.rowDelete.emit(row);
  }

  hasCustomTemplate(column: TableColumn): boolean {
    return column.customTemplate === true && this.customCellTemplates[column.key] !== undefined;
  }

  getCustomTemplate(column: TableColumn): TemplateRef<any> | null {
    return this.customCellTemplates[column.key] || null;
  }

  hasCustomHeaderTemplate(column: TableColumn): boolean {
    return column.customHeaderTemplate === true && this.customHeaderTemplates[column.key] !== undefined;
  }

  getCustomHeaderTemplate(column: TableColumn): TemplateRef<any> | null {
    return this.customHeaderTemplates[column.key] || null;
  }

  private computeDragPreviewLabel(row: TableRow): string {
    if (!this.dragPreviewLabelGetter) return '';

    return String(this.dragPreviewLabelGetter(row) || '');
  }

  onRowDrop(event: CdkDragDrop<TableRow[]>): void {
    if (!this.draggableRows) {
      return;
    }

    moveItemInArray(this._rows, event.previousIndex, event.currentIndex);
    this.rowsReordered.emit({
      rows: [...this._rows],
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex
    });
  }
}
